import { hex } from '@scure/base';
import { p2ms, p2pk, p2sh, p2tr, p2wpkh, Script, Transaction } from '@scure/btc-signer';
import { TransactionInput, TransactionOutput } from '@scure/btc-signer/psbt';
import { SigHash } from '@scure/btc-signer/transaction';
import { BtcAddressType } from 'market-maker.cm';
import { getBtcAddressType } from 'market-maker.cm/cweb_dist/offchain/shared';

import { UseUtxo } from '@/types';

import { BtcLikeNetwork, BtcLikeNetworkParams } from '../types';

import { getTxData, getTxHex, type ResultUtxo } from './api';
import {
  DEFAULT_INPUT_SEQUENCE,
  MOFN_MAX_EMBED_BYTES_PER_PUBKEY,
  OP_RETURN_BYTES,
  RBF_INPUT_SEQUENCE,
} from './constants';

export type OutScriptInfo = {
  type: string;
  address?: string;
  script: Uint8Array;
  redeemScript?: Uint8Array;
};

/**
 * Converts a compressed public key (33 bytes / 66 hex chars) to an x-only public key (32 bytes / 64 hex chars) for P2TR.
 * If the input is already an x-only key (32 bytes), it returns it unchanged.
 *
 * Compressed public keys:
 * - 66 hex characters (33 bytes)
 * - First 2 hex chars are "02" (even y-coordinate) or "03" (odd y-coordinate)
 * - Remaining 64 hex chars are the x-coordinate
 *
 * X-only public keys (for P2TR/Taproot):
 * - 64 hex characters (32 bytes)
 * - Contains only the x-coordinate
 *
 * @param publicKeyHex - Public key in hex format (either 66-char compressed or 64-char x-only)
 * @returns X-only public key in hex format (64 characters)
 * @throws Error if the public key format is invalid
 *
 * @see https://github.com/bitcoin/bips/blob/master/bip-0340.mediawiki#design
 */
export const toXOnlyPublicKey = (publicKeyHex: string): string => {
  const hexLength = publicKeyHex.length;

  // If it's already 64 hex chars (32 bytes), assume it's x-only and return as-is
  if (hexLength === 64) {
    return publicKeyHex;
  }

  // If it's 66 hex chars (33 bytes), check if it's a valid compressed public key
  if (hexLength === 66) {
    const prefix = publicKeyHex.slice(0, 2);

    // Check if it starts with "02" or "03" (compressed public key prefix)
    if (prefix !== '02' && prefix !== '03') {
      throw new Error(`Invalid compressed public key prefix: expected "02" or "03", got "${prefix}"`);
    }

    // Return the x-coordinate (remove the first 2 hex chars)
    return publicKeyHex.slice(2);
  }

  throw new Error(`Invalid public key length: expected 64 or 66 hex characters, got ${hexLength} characters`);
};

/**
 * Hacky trick to embed data in MofN multisig, by pushing data as the pubkey
 * Generating VALID pubKeyBytes nesting arbitrary data
 * We use 32 bytes as a compressed address (plus a byte to indicate parity of the missing coordinate).
 * 31 bytes we use for data, while the remaining 1 is set as the lowest byte such the pubkey is valid.
 * */
export const createPubkey = (data: string, networkParams: BtcLikeNetworkParams): Uint8Array => {
  for (let i = 0; i <= 255; i++) {
    const result = new Uint8Array([3, i, ...hex.decode(data)]);

    try {
      p2pk(result, networkParams);

      return result;
    } catch {
      continue;
    }
  }

  throw new Error('Could not find pubkey');
};

export const embedDataInOpReturn = (data: Uint8Array) => {
  if (data.byteLength > 80) {
    throw new Error('Data is too big to embed in OP_RETURN');
  }

  const hexPayloadToOpReturnEmbed = hex.encode(data);

  // padding to 8 bytes boundary
  const opReturnData = hex.decode(
    hexPayloadToOpReturnEmbed.concat('0'.repeat(16 - (hexPayloadToOpReturnEmbed.length % 16 || 16)))
  );

  return {
    script: Script.encode(['RETURN', opReturnData]),
    amount: BigInt(0),
  };
};

export const checkIsOfNMultiSig = (N: number, payload: Uint8Array) => {
  // 31 byte per compressed address, N times for MofN multisig
  if (payload.byteLength <= MOFN_MAX_EMBED_BYTES_PER_PUBKEY * N) {
    return true;
  }

  return false;
};

/**
 * M of N multisig, M is hardcoded to 2
 * 2of3 is the maximum MofN supported by the nodes (Standardness)
 *
 * @param N
 * @param payload
 * @returns
 */
export const createMofnP2ms = (N: number, payload: Uint8Array, networkParams: BtcLikeNetworkParams) => {
  // N pubKeys, 2 hex chars per byte
  const embedHexBytes = MOFN_MAX_EMBED_BYTES_PER_PUBKEY * N * 2;
  const hexPayload = hex.encode(payload);
  let hexPayloadToOfNMultisigEmbed = hexPayload.slice(0, embedHexBytes);

  if (hexPayloadToOfNMultisigEmbed.length <= embedHexBytes - 2) {
    hexPayloadToOfNMultisigEmbed = hexPayloadToOfNMultisigEmbed
      .concat('ff')
      .concat('0'.repeat(embedHexBytes - 2 - hexPayloadToOfNMultisigEmbed.length));
  }

  // 31 bytes per pubkey times 2 hex chars per byte = 62
  const multiSigKeys = hexPayloadToOfNMultisigEmbed.match(/.{1,62}/g)!.map((data) => createPubkey(data, networkParams));
  const multiSig = p2ms(2, multiSigKeys, true);

  return multiSig;
};

/**
 * @deprecated This function embeds data in transaction outputs using bare multisig pub keys and/or OP_RETURN.
 * Bare multisig should no longer be used - for payloads under 80 bytes, use `embedDataInOpReturn` instead.
 * This will be replaced entirely by `embedDataInOpReturn` in some future version.
 */
export const prepareEmbeddedDataBtcOutputs = (
  payload: Uint8Array,
  networkParams: BtcLikeNetworkParams,
  dustLimit: number
): TransactionOutput[] => {
  const result = [];

  // clone, instead of referencing
  let remainingPayload = payload.slice();

  while (remainingPayload.byteLength > 0) {
    if (remainingPayload.byteLength <= OP_RETURN_BYTES) {
      // Embed in OP_RETURN if remaining data is below 80 bytes
      result.push(embedDataInOpReturn(remainingPayload));
      remainingPayload = remainingPayload.slice(OP_RETURN_BYTES);
    } else if (checkIsOfNMultiSig(2, remainingPayload.slice(0, remainingPayload.byteLength - OP_RETURN_BYTES))) {
      // Embed in MultiSig 2 of 2 if it fits
      // (while considering that the remaining up-to 80 bytes can always go into OP_RETURN)
      // 2 pubKeys, 2 hex chars per byte
      const embedBytes = MOFN_MAX_EMBED_BYTES_PER_PUBKEY * 2;
      const multiSig = createMofnP2ms(2, remainingPayload.slice(0, embedBytes), networkParams);

      result.push({ script: multiSig.script, amount: BigInt(dustLimit) });

      // 3 pubKeys, 2 hex chars per byte
      remainingPayload = remainingPayload.slice(embedBytes);
    } else {
      // Embed in MultiSig 2 of 3 as the default
      // 3 pubKeys, 2 hex chars per byte
      const embedBytes = MOFN_MAX_EMBED_BYTES_PER_PUBKEY * 3;
      const multiSig = createMofnP2ms(3, remainingPayload.slice(0, embedBytes), networkParams);

      result.push({ script: multiSig.script, amount: BigInt(dustLimit) });

      remainingPayload = remainingPayload.slice(embedBytes);
    }
  }

  return result;
};

/**
 * Determines the appropriate sighash type based on the Bitcoin address type.
 *
 * For Taproot (P2TR) addresses, SIGHASH_DEFAULT (0) must be used as per BIP 341.
 * For all other address types (P2PKH, P2WPKH, P2SH, etc.), the provided sighash type is used.
 *
 * @param address - Bitcoin address
 * @param requestedSighash - The sighash type requested by the caller (e.g., SIGHASH_ALL)
 * @returns The appropriate sighash type for the address
 *
 * @see https://github.com/bitcoin/bips/blob/master/bip-0341.mediawiki#signature-validation-rules
 */
export const getSighashTypeForAddress = (address: string, requestedSighash: number): number => {
  const addressType = getBtcAddressType(address);

  // BIP 341: Taproot uses SIGHASH_DEFAULT (0) instead of SIGHASH_ALL (1)
  // Taproot signatures are 64 bytes (Schnorr) without explicit sighash byte
  if (addressType === BtcAddressType.p2tr) {
    return 0; // SIGHASH_DEFAULT
  }

  // For all other address types, use the requested sighash
  return requestedSighash;
};

/**
 * Converts signing input configuration to wallet-compatible format with address-specific optimizations.
 *
 * This function prepares PSBT inputs for signing by:
 * - Automatically selecting the correct sighash type based on address type (BIP 341 compliance)
 * - Configuring key tweaking behavior appropriately for each address type
 * - Formatting inputs according to wallet provider requirements (UniSat, Xverse, etc.)
 *
 * @param signInputs - Mapping of Bitcoin addresses to their input indices that need signing
 * @param allowedSignHash - The requested sighash type (e.g., SIGHASH_ALL=1, SINGLE_ANYONECANPAY=131)
 * @param disableTweakSigner - Whether to disable key tweaking for non-Taproot addresses
 *
 * @returns Array of signing input configurations with address-specific parameters
 *
 * @example
 * ```typescript
 * // Sign input 0 with P2WPKH address
 * convertSignInputsToInputType(
 *   { 'bc1qw508d6qejxtdg4y5r3zarvary0c5xw7kv8f3t4': [0] },
 *   ALLOWED_SIGN_HASH_TYPES.ALL,
 *   false
 * );
 * // Returns: [{ index: 0, address: 'bc1q...', sighashTypes: [1], disableTweakSigner: false }]
 *
 * // Sign input 1 with P2TR (Taproot) address
 * convertSignInputsToInputType(
 *   { 'bc1pd5ewfvwjnn6gdd2ffhpm39jx9ja0ykgw67a8jcnwxqxd7ydsgtgs5r56ad': [1] },
 *   ALLOWED_SIGN_HASH_TYPES.ALL,
 *   true
 * );
 * // Returns: [{ index: 1, address: 'bc1pd5e...', sighashTypes: [0] }]
 * // Note: SIGHASH_DEFAULT (0) used automatically, disableTweakSigner omitted for P2TR
 * ```
 *
 * @remarks
 * **Address Type Handling:**
 * - **P2TR (Taproot)**: Uses SIGHASH_DEFAULT (0) per BIP 341, omits disableTweakSigner
 * - **P2WPKH, P2SH, P2PKH**: Uses requested sighash, includes disableTweakSigner if provided
 *
 * **BIP 341 Compliance:**
 * Taproot signatures require SIGHASH_DEFAULT (0x00) for standard key path spending.
 * See: https://github.com/bitcoin/bips/blob/master/bip-0341.mediawiki#signature-validation-rules
 *
 * @see {@link getSighashTypeForAddress} for sighash type determination logic
 */
export const convertSignInputsToInputType = (
  signInputs: Record<string, number[]>,
  allowedSignHash: number,
  disableTweakSigner?: boolean
) => {
  const result = [];

  for (const address in signInputs) {
    const indexes = signInputs[address];
    const addressType = getBtcAddressType(address);
    const guardedSighashType = getSighashTypeForAddress(address, allowedSignHash);

    // For P2TR key path spending, omit disableTweakSigner to allow wallet default behavior
    const shouldDisableTweakSigner = addressType === BtcAddressType.p2tr ? undefined : disableTweakSigner;

    for (const index of indexes) {
      result.push({
        index,
        address,
        ...(shouldDisableTweakSigner !== undefined && { disableTweakSigner: shouldDisableTweakSigner }),
        ...(guardedSighashType !== undefined && { sighashTypes: [guardedSighashType] }),
      });
    }
  }

  return result;
};

/**
 * NestedSegwit transaction
 * P2SH-P2WPKH
 * Pay-to-Script-Hash Pay-to-Witness-Public-Key-Hash
 * This format allows the use of SegWit features while maintaining backward compatibility with older wallets that don't fully support SegWit.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const p2sh_p2wpkh = (pubKeyHex: string, networkParams: BtcLikeNetworkParams) => {
  const pubKeyBytes = hex.decode(pubKeyHex);
  const segWitKey = p2wpkh(pubKeyBytes, networkParams);

  return p2sh(segWitKey, networkParams);
};

export const getTransactionOutputs = (tx: Transaction) => {
  const outputs = [];

  for (let i = 0; i < tx.outputsLength; i++) {
    const output = tx.getOutput(i);

    outputs.push(output);
  }

  return outputs;
};

export const getTransactionInputs = (tx: Transaction) => {
  const inputs = [];

  for (let i = 0; i < tx.inputsLength; i++) {
    const input = tx.getInput(i);

    inputs.push(input);
  }

  return inputs;
};

export const getBlockedWalletUtxosByInputs = (tx: Transaction, walletUtxos: ResultUtxo[]): UseUtxo[] => {
  const txInputs = getTransactionInputs(tx);

  const blockedWalletUtxosByInputs = txInputs
    .map((input) => walletUtxos.find((utxo) => utxo.txid === hex.encode(input.txid!) && utxo.vout === input.index))
    .filter((utxo): utxo is ResultUtxo => Boolean(utxo)) // type narrowing
    .map(({ txid, vout }) => ({ l1TxId: txid, vout }));

  return blockedWalletUtxosByInputs;
};

export const calculateTxInputsValue = (txInputs: TransactionInput[], apiEndpoint: string): Promise<number> => {
  return Promise.all(
    txInputs
      .filter((txInput: TransactionInput) => txInput.txid && !Number.isNaN(txInput.index))
      .map(async (txInput: TransactionInput) => {
        return getTxData(hex.encode(txInput.txid!), apiEndpoint).then((txData) => {
          if (txData.vout.length > 0) {
            return txData.vout.at(txInput.index!)?.value || 0;
          }

          return 0;
        });
      })
  ).then((inputValues) => inputValues.reduce((acc, cur) => acc + cur, 0));
};

export const addInputUTXOsToTx = async ({
  tx,
  utxos,
  walletAddress,
  publicKey,
  useRbf = true,
  sighashType,
  network,
}: {
  tx: Transaction;
  utxos: ResultUtxo[];
  walletAddress: string;
  publicKey: string;
  useRbf?: boolean;
  sighashType?: SigHash;
  network: BtcLikeNetwork;
}): Promise<{
  txWithInputs: Transaction;
  inputValue: number;
}> => {
  const txWithInputs = tx.clone();

  // Add the selected UTXOs combination to the transaction
  const addressType = getBtcAddressType(walletAddress);

  let inputValue = 0;

  const sequence = useRbf ? RBF_INPUT_SEQUENCE : DEFAULT_INPUT_SEQUENCE;

  await Promise.all(
    utxos.map(async ({ txid, vout, value }) => {
      inputValue += value;

      switch (addressType) {
        case BtcAddressType.p2pkh: {
          const fullTxHex = await getTxHex(txid, network.MempoolApiEndpoint);

          txWithInputs.addInput({
            txid,
            index: vout,
            nonWitnessUtxo: fullTxHex,
            sequence,
            ...(sighashType && { sighashType }),
          });
          break;
        }
        case BtcAddressType.p2sh: {
          const p2shScript = p2sh(p2wpkh(hex.decode(publicKey), network.Settings), network.Settings);

          txWithInputs.addInput({
            txid,
            index: vout,
            witnessUtxo: { script: p2shScript.script, amount: BigInt(value) },
            redeemScript: p2shScript.redeemScript,
            sequence,
            ...(sighashType && { sighashType }),
          });
          break;
        }
        case BtcAddressType.p2wpkh: {
          const p2wpkhScript = p2wpkh(hex.decode(publicKey), network.Settings);

          txWithInputs.addInput({
            txid,
            index: vout,
            witnessUtxo: { script: p2wpkhScript.script, amount: BigInt(value) },
            sequence,
            ...(sighashType && { sighashType }),
          });
          break;
        }
        case BtcAddressType.p2wsh: {
          throw new Error('P2WSH addresses are not supported as a signer');
          break;
        }
        case BtcAddressType.p2tr: {
          // P2TR requires x-only public key (32 bytes), not compressed (33 bytes)
          // https://github.com/bitcoin/bips/blob/master/bip-0340.mediawiki#design
          const xOnlyPubKeyHex = toXOnlyPublicKey(publicKey);
          const p2trScript = p2tr(hex.decode(xOnlyPubKeyHex), undefined, network.Settings);

          txWithInputs.addInput({
            txid,
            index: vout,
            witnessUtxo: { script: p2trScript.script, amount: BigInt(value) },
            tapInternalKey: p2trScript.tapInternalKey,
            sequence,
            ...(sighashType && { sighashType }),
          });
          break;
        }

        default:
          throw new Error(`${addressType} addresses are not supported`);
      }
    })
  );

  return { txWithInputs, inputValue };
};
